/**
 * auth.tsx — 인증 컨텍스트 프로바이더
 *
 * httpOnly 쿠키 기반 인증. 클라이언트에서 토큰에 직접 접근하지 않고,
 * /auth/me API 호출로 인증 상태를 확인한다.
 * 쿠키는 브라우저가 자동으로 관리하므로 localStorage 조작이 불필요.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiFetch } from './api.js';

interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: 'USER' | 'ADMIN';
  premiumPlan: 'FREE' | 'PREMIUM';
  aiCredits: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider: 'kakao' | 'naver') => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /** /auth/me 호출로 인증 상태 확인 (쿠키 자동 전송) */
  const fetchUser = useCallback(async () => {
    try {
      const res = await apiFetch<AuthUser>('/auth/me');
      if (res.success) {
        setUser(res.data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /** OAuth 로그인 — API의 OAuth 시작 URL로 이동 */
  const login = useCallback((provider: 'kakao' | 'naver') => {
    const apiBase = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiBase}/auth/${provider}`;
  }, []);

  /** 로그아웃 — 서버에서 쿠키 삭제 + DB 토큰 무효화 */
  const logout = useCallback(async () => {
    setUser(null);
    apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
